import { cn } from '@/lib/utils';
import { Row } from '@tanstack/react-table';
import { ChevronDown, ChevronRight, LucideIcon } from 'lucide-react';
import { ReactNode } from 'react';
import { TREE_INDENT_PER_LEVEL_PX } from './tree.constants';
import { setSubtreeExpanded } from './tree.utils';

interface TreeCellLeadingProps<TData> {
  row: Row<TData>;
  /** Optional row-type icon shown after the chevron (e.g. folder / file). */
  icon?: LucideIcon | null;
  /**
   * Children rendered after the chevron + icon. Typically the row's label.
   */
  children: ReactNode;
  className?: string;
  /**
   * Indent width per depth level, in pixels. Defaults to the shared
   * TREE_INDENT_PER_LEVEL_PX so desktop leading indent and the mobile
   * summary row-2 pad stay in lockstep. Override only if a consumer
   * deliberately diverges (and mirror it on the mobile side).
   */
  perDepthIndentPx?: number;
}

/**
 * Leading content for the first column of a tree-mode DataTable row.
 * Renders [chevron][icon][children]; the chevron toggles row expansion when
 * the row has subRows, and is a sized spacer otherwise so titles align across
 * leaf and branch rows.
 *
 * Plain click toggles this row one level. Alt/Option-click toggles the whole
 * subtree (expand/collapse every descendant) — the macOS Finder disclosure
 * convention.
 *
 * The chevron click is stopped from propagating so that toggling expansion
 * doesn't also trigger row-navigation or row-selection click handlers.
 */
export function TreeCellLeading<TData>({
  row,
  icon: Icon,
  children,
  className,
  perDepthIndentPx = TREE_INDENT_PER_LEVEL_PX,
}: TreeCellLeadingProps<TData>) {
  const canExpand = row.getCanExpand();
  const isExpanded = row.getIsExpanded();
  const Chevron = isExpanded ? ChevronDown : ChevronRight;

  return (
    <div
      className={cn('flex min-w-0 items-center gap-1', className)}
      style={
        perDepthIndentPx
          ? { paddingLeft: row.depth * perDepthIndentPx }
          : undefined
      }
    >
      {canExpand ? (
        // Visual size stays size-4 so depth indent / row-2 alignment math
        // stays correct, but a transparent ::before pseudo-element extends
        // the hit area by 8px in every direction (effective ~32x32 target,
        // well above mobile-tap thresholds). touch-action manipulation
        // suppresses the legacy 300ms tap-delay on touchscreens.
        <button
          type="button"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          title={
            isExpanded
              ? 'Collapse (Alt-click: whole subtree)'
              : 'Expand (Alt-click: whole subtree)'
          }
          aria-expanded={isExpanded}
          onClick={(e) => {
            e.stopPropagation();
            if (e.altKey) setSubtreeExpanded(row, !isExpanded);
            else row.toggleExpanded();
          }}
          className="text-muted-foreground hover:text-foreground relative inline-flex size-4 shrink-0
            cursor-pointer touch-manipulation items-center justify-center before:absolute
            before:-inset-2 before:content-['']"
        >
          <Chevron className="size-3.5" />
        </button>
      ) : (
        <span className="inline-flex size-4 shrink-0" aria-hidden />
      )}
      {Icon && <Icon className="text-muted-foreground size-3.5 shrink-0" />}
      <div className="min-w-0 flex-1 truncate">{children}</div>
    </div>
  );
}
